import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/* ── Types ────────────────────────────────────────────────────── */
type Protocol = { slug: string; label: string };
type TocItem  = { level: number; text: string; id: string };

/* ── Helpers ──────────────────────────────────────────────────── */

/** Same algorithm used when assigning IDs to rendered headings */
const slugify = (text: string) =>
  text.toLowerCase()
      .replace(/[^\w\s-]/g, '')   // keep word chars, spaces, hyphens
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

/** Extract plain text from React children (for heading ID generation) */
function nodeToText(node: React.ReactNode): string {
  if (!node) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (typeof node === 'object' && 'props' in (node as object))
    return nodeToText((node as React.ReactElement).props.children);
  return '';
}

/** Headings to omit from the sidebar TOC */
const TOC_SKIP_PREFIX = [
  'table of contents',
  'this is a reference',
];

/** Build TOC from raw markdown text */
function extractTOC(md: string): TocItem[] {
  return md.split('\n').flatMap(line => {
    const m = line.match(/^(#{1,3}) (.+)/);
    if (!m) return [];
    // Strip inline markdown (code, bold, links) from label text
    const raw = m[2]
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .trim();
    const lower = raw.toLowerCase();
    if (TOC_SKIP_PREFIX.some(p => lower.startsWith(p))) return [];
    return [{ level: m[1].length, text: raw, id: slugify(raw) }];
  });
}

/** Extract protocol version string from markdown content */
function extractVersion(md: string): string {
  const m = md.match(/ridelayer\/[\d-]+/);
  return m ? m[0] : '';
}

/**
 * Remove the "## Table of Contents" section that some protocol files include.
 * The sidebar provides navigation, so the embedded TOC is redundant.
 */
function stripTOC(md: string): string {
  const lines = md.split('\n');
  const start = lines.findIndex(l => /^## Table of Contents\s*$/.test(l.trim()));
  if (start === -1) return md;
  const end = lines.findIndex((l, i) => i > start && /^#{1,6} /.test(l));
  if (end === -1) return md;
  return [...lines.slice(0, start), ...lines.slice(end)].join('\n');
}

/* ── Reusable sub-components ──────────────────────────────────── */

/** Fenced code block with a copy button */
function PreBlock({ children }: { children: React.ReactNode }) {
  const ref    = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(ref.current?.textContent?.trim() ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="code-block">
      <button className="copy-btn" onClick={copy}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre ref={ref}>{children}</pre>
    </div>
  );
}

/* ── Markdown component map ───────────────────────────────────── */
// Heading IDs use slugify() so they match extractTOC() and scroll-spy targets.

const MD_COMPONENTS = {
  pre: PreBlock,

  // Inline code keeps the global "code" style; block code inside PreBlock
  // gets the code-block treatment automatically.
  code({ className, children, ...props }: React.HTMLAttributes<HTMLElement> & { className?: string }) {
    return <code className={className} {...props}>{children}</code>;
  },

  h1({ children }: { children: React.ReactNode }) {
    return <h1 id={slugify(nodeToText(children))}>{children}</h1>;
  },
  h2({ children }: { children: React.ReactNode }) {
    return <h2 id={slugify(nodeToText(children))}>{children}</h2>;
  },
  h3({ children }: { children: React.ReactNode }) {
    return <h3 id={slugify(nodeToText(children))}>{children}</h3>;
  },

  // Tables get the existing param-table styling
  table({ children }: { children: React.ReactNode }) {
    return <table className="param-table">{children}</table>;
  },

  // External links open in a new tab
  a({ href, children }: { href?: string; children: React.ReactNode }) {
    const isExternal = href?.startsWith('http');
    return (
      <a href={href} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {children}
      </a>
    );
  },

  // Blockquotes render as callouts (re-use existing .docs-callout style)
  blockquote({ children }: { children: React.ReactNode }) {
    return <div className="docs-callout">{children}</div>;
  },
};

/* ── DocsPage ─────────────────────────────────────────────────── */

export default function DocsPage() {
  const { device }   = useParams<{ device: string }>();
  const navigate     = useNavigate();

  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [markdown,  setMarkdown]  = useState('');
  const [loading,   setLoading]   = useState(true);
  const [active,    setActive]    = useState('');

  /* Fetch list of available protocol slugs once on mount */
  useEffect(() => {
    fetch('/api/protocols')
      .then(r => r.json())
      .then((data: Protocol[]) => setProtocols(data))
      .catch(console.error);
  }, []);

  /* Fetch markdown content whenever the selected device changes */
  useEffect(() => {
    if (!device) return;
    setLoading(true);
    setMarkdown('');
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });

    fetch(`/api/protocols/${device}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(md => {
        setMarkdown(stripTOC(md));
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [device]);

  /* Scroll-spy: highlight the active TOC item as user scrolls */
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('.docs-content [id]');
    if (!els.length) return;

    const obs = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );

    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [markdown]);   // re-observe whenever content changes

  const toc     = useMemo(() => extractTOC(markdown), [markdown]);
  const version = useMemo(() => extractVersion(markdown), [markdown]);

  /* Redirect /docs → first available device */
  if (!device) {
    if (protocols.length === 0) return <div className="docs-layout"><aside className="docs-sidebar" /></div>;
    return <Navigate to={`/docs/${protocols[0].slug}`} replace />;
  }

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="docs-layout">
      {/* ── Left sidebar ──────────────────────────────────────── */}
      <aside className="docs-sidebar">
        <div className="docs-sidebar-title">MQTT Protocol</div>
        {version && <div className="docs-sidebar-version">{version}</div>}

        {/* Device type switcher */}
        <div className="docs-device-list">
          {protocols.map(p => (
            <button
              key={p.slug}
              className={`docs-device-btn${p.slug === device ? ' active' : ''}`}
              onClick={() => navigate(`/docs/${p.slug}`)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Table of contents */}
        {toc.length > 0 && (
          <>
            <div className="docs-toc-label">On this page</div>
            <nav className="docs-nav">
              {toc.map(item => (
                <button
                  key={`${item.id}-${item.level}`}
                  className={[
                    'docs-nav-item',
                    item.level === 2 ? 'docs-nav-child' : '',
                    item.level === 3 ? 'docs-nav-grandchild' : '',
                    active === item.id ? 'active' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => scrollTo(item.id)}
                >
                  {item.text}
                </button>
              ))}
            </nav>
          </>
        )}
      </aside>

      {/* ── Main content ──────────────────────────────────────── */}
      <main className="docs-main">
        {loading ? (
          <div className="docs-content">
            <p className="hint">Loading…</p>
          </div>
        ) : (
          <div className="docs-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS as any}>
              {markdown}
            </ReactMarkdown>
            <div style={{ height: '4rem' }} />
          </div>
        )}
      </main>
    </div>
  );
}
